/**
 * Message image storage helpers
 *
 * Handles chat message image upload and retrieval.
 *
 * Primary responsibilities:
 * - Route browser uploads through authenticated upload API
 * - Keep desktop direct storage uploads available
 * - Resolve resilient signed URLs for stored images
 *
 * Dependencies:
 * - Supabase message_images storage bucket
 * - authenticated message image upload API
 * - signed URL manager
 */

import { supabase } from '@/lib/supabase/browser-client'
import { getMessageImageFromStorage as getResilientMessageImage } from '@/lib/supabase/signed-url-manager'
import { getDesktopAccessTokenSupabaseClient } from '@/lib/desktop-supabase-client'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'

const getMessageImagesClient = () => getDesktopAccessTokenSupabaseClient() || supabase

type MessageImageUploadOptions = {
  uploadLimitChatId?: string | null
  uploadLimitSpaceId?: string | null
  workspaceId?: string | null
}

const readUploadError = async (response: Response) => {
  const responseText = await response.text()

  if (!responseText) {
    return 'Error uploading image'
  }

  try {
    const payload = JSON.parse(responseText) as unknown

    if (
      typeof payload === 'object' &&
      payload !== null &&
      'error' in payload &&
      typeof payload.error === 'string'
    ) {
      return payload.error
    }
  } catch {
    return responseText
  }

  return responseText
}

export const uploadMessageImage = async (
  path: string,
  image: File,
  options: MessageImageUploadOptions = {}
) => {
  const bucket = 'message_images'

  const imageSizeLimit = 500 * 1024 * 1024 // 500MB

  if (image.size > imageSizeLimit) {
    throw new Error(`Image must be less than ${imageSizeLimit / 1000000}MB`)
  }

  if (typeof window !== 'undefined' && !getDesktopAccessTokenSupabaseClient()) {
    const SIGNED_UPLOAD_THRESHOLD = 4 * 1024 * 1024 // 4MB

    if (image.size > SIGNED_UPLOAD_THRESHOLD) {
      // Use signed URL upload to bypass Vercel body size limit
      const signResponse = await authenticatedFetch(
        '/api/message-images/sign-upload',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            path,
            contentType: image.type || 'image/jpeg',
            fileSize: image.size,
            ...(options.workspaceId
              ? { workspace_id: options.workspaceId }
              : {}),
            ...(options.uploadLimitChatId
              ? { upload_limit_chat_id: options.uploadLimitChatId }
              : {}),
            ...(options.uploadLimitSpaceId
              ? { upload_limit_space_id: options.uploadLimitSpaceId }
              : {})
          })
        }
      )

      if (!signResponse.ok) {
        throw new Error(await readUploadError(signResponse))
      }

      const { signedUrl } = await signResponse.json()

      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': image.type || 'image/jpeg' },
        body: image
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to storage')
      }

      return path
    }

    // Standard FormData upload for files <= 4MB
    const formData = new FormData()
    formData.append('path', path)
    formData.append('file', image)

    if (options.workspaceId) {
      formData.append('workspace_id', options.workspaceId)
    }

    if (options.uploadLimitChatId) {
      formData.append('upload_limit_chat_id', options.uploadLimitChatId)
    }

    if (options.uploadLimitSpaceId) {
      formData.append('upload_limit_space_id', options.uploadLimitSpaceId)
    }

    const response = await authenticatedFetch('/api/message-images/upload', {
      method: 'POST',
      body: formData
    })

    if (!response.ok) {
      throw new Error(await readUploadError(response))
    }

    return path
  }

  const { error } = await getMessageImagesClient().storage.from(bucket).upload(path, image, {
    upsert: true
  })

  if (error) {
    throw new Error('Error uploading image')
  }

  return path
}

// Use the resilient signed URL manager for better JWT token handling
export const getMessageImageFromStorage = getResilientMessageImage

/**
 * Download image from storage and convert to base64 data URL
 * Used for OpenAI API calls to avoid authentication issues with signed URLs
 */
export const getMessageImageAsBase64 = async (filePath: string): Promise<string> => {
  try {
    // Get signed URL first
    const signedUrl = await getResilientMessageImage(filePath)

    // Download the image
    const response = await fetch(signedUrl)
    if (!response.ok) {
      throw new Error(`Failed to download image: ${response.status}`)
    }

    // Convert to blob and then base64
    const blob = await response.blob()
    const arrayBuffer = await blob.arrayBuffer()
    const base64 = Buffer.from(arrayBuffer).toString('base64')

    // Determine MIME type
    const mimeType = blob.type || 'image/jpeg'

    // Return as data URL
    return `data:${mimeType};base64,${base64}`
  } catch (error) {
    console.error('Error converting image to base64:', error)
    throw new Error(
      `Failed to convert image to base64: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}
